/* -------------------------- Design imports start -------------------------- */
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material'
import { Box, Button, FormControl, FormHelperText, IconButton, TextField } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from 'react-i18next'

import LogTool from '../../logger/logTools'
/* ------------------------- Functional functions end ------------------------- */

type Props = {
  id?: string
  value: string | number | undefined
  label: string
  onChange: (value: string) => void
  validation?: boolean
  errorMessage?: string
  disabled?: boolean
  required?: boolean
  showError?: boolean
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function QuantityMultiInput(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    id,
    label,
    value,
    onChange,
    disabled = false,
    required = false,
    errorMessage = '',
    validation = true,
    showError = true,
  } = props

  const log = new LogTool({ context: 'QuantityMultiInput', enable: false })
  const { t } = useTranslation(['common', 'request'])
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  // Parse comma-separated string to array of numbers
  const parseQuantityString = (val: string | number | undefined): number[] => {
    if (!val) return [1] // Default to one quantity input with value 1
    if (typeof val === 'number') return [val]
    const str = String(val).trim()
    if (str === '') return [1]
    return str
      .split(',')
      .map(q => q.trim())
      .filter(q => q !== '')
      .map(q => {
        const num = parseInt(q, 10)
        return isNaN(num) ? 1 : num
      })
      .filter(num => num > 0)
  }

  const [quantities, setQuantities] = useState<number[]>(() => parseQuantityString(value))
  const [invalidCharFields, setInvalidCharFields] = useState<Record<number, boolean>>({})
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  // Update quantities when value prop changes (e.g., when loading existing data)
  useEffect(() => {
    const parsed = parseQuantityString(value)
    if (JSON.stringify(parsed) !== JSON.stringify(quantities)) {
      setQuantities(parsed)
    }
  }, [value])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  const convertToCommaString = (qtyArray: number[]): string => {
    return qtyArray.filter(q => q > 0).join(',')
  }

  const sanitizeNumericInput = (value: string): string => {
    return value.replace(/[^0-9]/g, '')
  }

  const handleQuantityChange = (index: number, newValue: string) => {
    const sanitizedValue = sanitizeNumericInput(newValue)
    const hasInvalidChars = /[^0-9]/.test(newValue)
    setInvalidCharFields(prev => {
      if (!hasInvalidChars && !(index in prev)) {
        return prev
      }
      const next = { ...prev }
      if (hasInvalidChars) {
        next[index] = true
      } else {
        delete next[index]
      }
      return next
    })

    const numValue = sanitizedValue === '' ? 0 : parseInt(sanitizedValue, 10)

    const newQuantities = [...quantities]
    newQuantities[index] = numValue > 0 ? numValue : 0
    setQuantities(newQuantities)

    const validQuantities = newQuantities.filter(q => q > 0)
    if (validQuantities.length > 0) {
      onChange(convertToCommaString(newQuantities))
    } else {
      onChange('')
    }
  }

  const handleAddQuantity = () => {
    const newQuantities = [...quantities, 0] // New quantities start empty (0), not with default 1
    setQuantities(newQuantities)
    // Don't call onChange yet since the new quantity is 0 (empty)
  }

  const handleRemoveQuantity = (index: number) => {
    if (quantities.length <= 1) return // Keep at least one input
    const newQuantities = quantities.filter((_, i) => i !== index)
    setQuantities(newQuantities)
    onChange(convertToCommaString(newQuantities))
    setInvalidCharFields(prev => {
      const nextEntries: Record<number, boolean> = {}
      Object.entries(prev).forEach(([key, value]) => {
        const keyIndex = Number(key)
        if (keyIndex === index) return
        nextEntries[keyIndex > index ? keyIndex - 1 : keyIndex] = value
      })
      return nextEntries
    })
  }

  const hasInvalidCharError = Object.keys(invalidCharFields).length > 0

  const hasDuplicates = (qtyArray: number[] = quantities): boolean => {
    const validQuantities = qtyArray.filter(q => q > 0)
    return new Set(validQuantities).size !== validQuantities.length
  }

  const isFieldDuplicate = (index: number): boolean => {
    if (quantities[index] <= 0) return false
    return quantities.filter((q, i) => q === quantities[index] && q > 0 && i !== index).length > 0
  }

  const isValid = (qtyArray: number[] = quantities): boolean => {
    if (!validation) return true
    if (required && qtyArray.length === 0) return false
    if (required && qtyArray.every(q => q <= 0)) return false
    if (hasDuplicates(qtyArray)) return false // Check for duplicate quantities
    if (hasInvalidCharError) return false
    return qtyArray.every(q => q > 0 || q === 0) // Allow 0 temporarily during input
  }

  const getErrorMessage = (): string => {
    if (hasInvalidCharError) {
      return t('request:feedback.error.onlyNumbers')
    }
    if (hasDuplicates()) {
      return t('request:feedback.error.duplicateQuantity')
    }
    if (required && quantities.every(q => q <= 0)) {
      return t('request:feedback.error.atLeastOneQuantityRequired')
    }
    return errorMessage || (required ? 'At least one quantity is required' : 'Invalid quantity')
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  // Calculate error state based on current quantities - this will be recalculated on every render
  const hasDuplicatesError = hasDuplicates()
  const hasRequiredError = required && quantities.every(q => q <= 0)
  const hasError = showError && (hasDuplicatesError || hasRequiredError || hasInvalidCharError)
  /* -------------------------- Render constants end -------------------------- */

  log.debug('GLOBAL', 'value ->', value, ', quantities ->', quantities, ', hasDuplicatesError ->', hasDuplicatesError, ', hasError ->', hasError)

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <FormControl fullWidth variant="outlined" error={hasError}>
      <Box sx={{ width: '100%' }}>
        {quantities.map((quantity, index) => {
          const fieldHasInvalidChars = Boolean(invalidCharFields[index])
          const fieldIsDuplicate = isFieldDuplicate(index)
          const fieldHasError =
            fieldHasInvalidChars || fieldIsDuplicate || (required && index === 0 && quantity <= 0)

          return (
            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1, width: '100%' }}>
              <TextField
                type="text"
                variant="outlined"
                label={index === 0 ? label : `${label} ${index + 1}`}
                id={id ? `${id}-${index}` : `${label}-${index}`}
                value={quantity || ''}
                onChange={e => handleQuantityChange(index, e.target.value)}
                disabled={disabled}
                required={required && index === 0}
                error={showError && fieldHasError}
                helperText={
                  !showError
                    ? ''
                    : fieldHasInvalidChars
                    ? t('request:feedback.error.onlyNumbers')
                    : ''
                }
                fullWidth
                inputProps={{
                  min: 1,
                  step: 1,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  'aria-label': `Quantity ${index + 1}`,
                }}
                sx={{ mr: quantities.length > 1 ? 1 : 0 }}
              />
              {quantities.length > 1 && (
                <IconButton
                  onClick={() => handleRemoveQuantity(index)}
                  disabled={disabled}
                  color="error"
                  aria-label={`Remove quantity ${index + 1}`}
                  sx={{ mt: 0.5, ml: 0.5 }}
                >
                  <RemoveCircleOutline />
                </IconButton>
              )}
            </Box>
          )
        })}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, width: '100%' }}>
          <Button
            onClick={handleAddQuantity}
            disabled={disabled}
            variant="outlined"
            color="primary"
            startIcon={<AddCircleOutline />}
            aria-label={t('request:interaction.button.newQuantity')}
            size="small"
          >
            {t('request:interaction.button.newQuantity')}
          </Button>
        </Box>
      </Box>
      {hasError && (
        <FormHelperText error variant="outlined">
          {getErrorMessage()}
        </FormHelperText>
      )}
    </FormControl>
  )
}
